import type { CategoryKey } from './categories';

export * from './categories';

/** Category key → percentage. Keys must be valid CategoryKey values. */
export type Allocations = Partial<Record<CategoryKey, number>>;

export const languages = {
	Spanish: 'SPA',
	English: 'ENG',
	Catalan: 'CAT',
	Euskera: 'EUS',
	Gallego: 'GLG',
	German: 'GER',
	French: 'FRE',
	Korean: 'KOR',
	Chinese: 'CHI',
	Japanese: 'JPN',
	Portuguese: 'POR',
	Russian: 'RUS',
	Italian: 'ITA',
	Dutch: 'DUT',
	Greek: 'GRE',
} as const;

export type Language = (typeof languages)[keyof typeof languages];

export const locales = [
	'es',
	'en',
	'ca',
	'eu',
	'gl',
	'de',
	'fr',
	'ko',
	'zh',
	'ja',
	'pt',
	'ru',
	'it',
	'nl',
	'el',
] as const;

export type Locale = (typeof locales)[number];

export const localeToLanguage: Record<Locale, Language> = {
	es: languages.Spanish,
	en: languages.English,
	ca: languages.Catalan,
	eu: languages.Euskera,
	gl: languages.Gallego,
	de: languages.German,
	fr: languages.French,
	ko: languages.Korean,
	zh: languages.Chinese,
	ja: languages.Japanese,
	pt: languages.Portuguese,
	ru: languages.Russian,
	it: languages.Italian,
	nl: languages.Dutch,
	el: languages.Greek,
};

export function localeToLang(locale: string): Language | undefined {
	const normalizedLocale = locale.trim().toLowerCase();
	return localeToLanguage[normalizedLocale as keyof typeof localeToLanguage];
}

export enum merchantOrigin {
	backoffice,
	onboarding,
	onboardingMagento,
	onboardingPrestashop,
	onboardingTpv,
}

export const channelTypes = ['Corporate', 'Tpv', 'Widget', 'API', 'Custom'] as const;

export type ChannelType = (typeof channelTypes)[number];

export const WORLDCOO_MERCHANT_ID = '997d0641-65ab-4897-9719-e4977f733527';

// Routes Patagonia portal donations to the `thanks-donation-portal-patagonia` email template (VSNI-42).
export const PATAGONIA_MERCHANT_ID = 'fd9bfc6d-5853-491a-8413-06373db4be20';

/**
 * Ceiling for a donation amount, monthly for a subscription. Exclusive: an amount has to stay
 * strictly below it, which is why the copy reads "less than" and the checks use `>=`.
 *
 * Denominated in euros, so a check against it only holds while the amount being checked is in euros
 * too. The number is formatted per locale wherever it reaches a donor, so do not spell it out in a
 * comment or a message: `es` renders it `3000` and `en` renders it `3,000`.
 */
export const DONATION_AMOUNT_LIMIT = 3000;

export type DocumentType = 'id' | 'taxId' | 'passport' | 'nie' | 'others';

export const applicationTypes = [
	'merchantDefault',
	'api',
	'widget',
	'backOffice',
	'portal',
	'tpv',
	'recurringDonation',
	'file',
	'paymentWidget',
	'merchantImplementation',
	'extra',
	'shopify',
] as const;

export type ApplicationType = (typeof applicationTypes)[number];

export const commercialTracks = ['pre-plan-b', 'plan-b'] as const;
export type CommercialTrack = (typeof commercialTracks)[number];

/** NGOs without a declared commercial track are on the introductory pre-plan-b track. */
export function resolveCommercialTrack(commercialTrack: CommercialTrack | undefined): CommercialTrack {
	return commercialTrack ?? 'pre-plan-b';
}

/** The Worldcoo fee percentage an NGO pays, driven by its commercial track. */
export function getWorldcooFeeForTrack(commercialTrack: CommercialTrack): number {
	return commercialTrack === 'plan-b' ? 15 : 12;
}

export const commercialTrackLabels: Record<CommercialTrack, string> = {
	'plan-b': 'Plan B',
	'pre-plan-b': 'Pre-Plan B',
};

export const campaignStatuses = ['sketch', 'scheduled', 'active', 'finished', 'disabled'] as const;
export type CampaignStatus = (typeof campaignStatuses)[number];

export const campaignStatusLabels: Record<CampaignStatus, string> = {
	sketch: 'Sketch',
	scheduled: 'Scheduled',
	active: 'Active',
	finished: 'Finished',
	disabled: 'Disabled',
};

export const languageToLocale = {
	[languages.Spanish]: 'es',
	[languages.English]: 'en',
	[languages.Catalan]: 'ca',
	[languages.Euskera]: 'eu',
	[languages.Gallego]: 'gl',
	[languages.German]: 'de',
	[languages.French]: 'fr',
	[languages.Korean]: 'ko',
	[languages.Chinese]: 'zh',
	[languages.Japanese]: 'ja',
	[languages.Portuguese]: 'pt',
	[languages.Russian]: 'ru',
	[languages.Italian]: 'it',
	[languages.Dutch]: 'nl',
	[languages.Greek]: 'el',
} as const satisfies Record<Language, Locale>;
